import React, { useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { X } from 'lucide-react';

// Base InfoComponent
const InfoComponent = forwardRef(({ apiService, alertService, dataContext, getItemName, fetchItemDetails, renderInfo }, ref) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemDetails, setItemDetails] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const modalRef = useRef(null);

  useImperativeHandle(ref, () => ({
    async openModal(item) {
      setSelectedItem(item);
      setIsLoading(true);
      try {
        const details = await fetchItemDetails(item, apiService);
        setItemDetails(details);
        setShowModal(true);
      } catch (error) {
        console.error(`Error fetching ${getItemName()} details:`, error);
        alertService.showAlert(`Failed to fetch ${getItemName()} details: ${error.message}`, 'error');
      } finally {
        setIsLoading(false);
      }
    },
    closeModal() {
      setSelectedItem(null);
      setItemDetails(null);
      setShowModal(false);
    },
  }));

  const handleOverlayClick = (e) => {
    if (e.target === modalRef.current) {
      ref.current.closeModal();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      ref.current.closeModal();
    }
  };

  if (!showModal) return null;

  return (
    <div
      className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto"
      ref={modalRef}
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-labelledby={`info-${getItemName()}-title`}
      aria-modal="true"
      tabIndex={-1}
    >
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md my-8 max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center mb-4 flex-shrink-0">
          <h3 id={`info-${getItemName()}-title`} className="text-xl font-bold text-gray-900">
            {getItemName()} Details
          </h3>
          <button
            onClick={() => ref.current.closeModal()}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Close modal"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center items-center py-8">
              <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
          ) : (
            renderInfo({ selectedItem, itemDetails })
          )}
        </div>
        
        <div className="mt-6 flex justify-end flex-shrink-0">
          <button
            onClick={() => ref.current.closeModal()}
            className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
});

// PatientInfoComponent
const PatientInfoComponent = forwardRef(({ apiService, alertService, patientContext }, ref) => {
  const getItemName = () => 'Patient';

  const fetchItemDetails = async (item, apiService) => {
    const query = `
      query GetPatientDetails($patientId: Int!) {
        patient(patientId: $patientId) {
          id
          patientId
          name
          age
          phone
          department
          symptoms
          status
          organization {
            id
            name
          }
        }
      }
    `;
    const response = await apiService.graphqlRequest(query, { patientId: item.id });
    if (!response || !response.patient || response.errors) {
      throw new Error(response.errors?.[0]?.message || 'Failed to fetch patient details');
    }
    return response.patient;
  };

  const renderInfo = ({ selectedItem, itemDetails }) => (
    <div className="space-y-4">
      <div>
        <h4 className="text-sm font-medium text-gray-700">Patient ID</h4>
        <p className="text-gray-900">{itemDetails?.patientId || 'N/A'}</p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700">Name</h4>
        <p className="text-gray-900">{itemDetails?.name || 'N/A'}</p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700">Age</h4>
        <p className="text-gray-900">{itemDetails?.age || 'N/A'}</p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700">Phone</h4>
        <p className="text-gray-900">{itemDetails?.phone || 'N/A'}</p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700">Department</h4>
        <p className="text-gray-900">{itemDetails?.department || 'N/A'}</p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700">Symptoms</h4>
        <p className="text-gray-900">{itemDetails?.symptoms || 'N/A'}</p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700">Status</h4>
        <p className="text-gray-900">{itemDetails?.status || 'N/A'}</p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700">Organization</h4>
        <p className="text-gray-900">{itemDetails?.organization?.name || 'N/A'}</p>
      </div>
      {itemDetails?.medicalRecords?.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700">Medical Records</h4>
          <ul className="list-disc pl-5 space-y-2">
            {itemDetails.medicalRecords.map(record => (
              <li key={record.id} className="text-gray-900">
                <p><strong>Date:</strong> {new Date(record.recordDate).toLocaleDateString()}</p>
                <p><strong>Diagnosis:</strong> {record.diagnosis || 'N/A'}</p>
                <p><strong>Treatment:</strong> {record.treatment || 'N/A'}</p>
                <p><strong>Medications:</strong> {record.medicationsPrescribed || 'N/A'}</p>
                <p><strong>Notes:</strong> {record.notes || 'N/A'}</p>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );

  return (
    <InfoComponent
      ref={ref}
      apiService={apiService}
      alertService={alertService}
      dataContext={patientContext}
      getItemName={getItemName}
      fetchItemDetails={fetchItemDetails}
      renderInfo={renderInfo}
    />
  );
});

// AppointmentInfoComponent
const AppointmentInfoComponent = forwardRef(({ apiService, alertService, appointmentContext }, ref) => {
  const getItemName = () => 'Appointment';

const fetchItemDetails = async (item, apiService) => {
  const query = `
    query GetAppointmentDetails($id: Int!) {
      appointment(appointmentId: $id) {
        id
        patientId
        doctorId
        appointmentTime
        department
        appointmentType
        status
        notes
        patient { id name }
        doctor { id name }
        organization { id name }
      }
    }
  `;
  const response = await apiService.graphqlRequest(query, { id: item.id });
  if (!response || !response.appointment || response.errors) {
    throw new Error(response.errors?.[0]?.message || 'Failed to fetch appointment details');
  }
  return response.appointment;
};


  const renderInfo = ({ selectedItem, itemDetails }) => (
    <div className="space-y-4">
      <div>
        <h4 className="text-sm font-medium text-gray-700">Patient</h4>
        <p className="text-gray-900">{itemDetails?.patient?.name || 'N/A'}</p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700">Doctor</h4>
        <p className="text-gray-900">{itemDetails?.doctor?.name || 'N/A'}</p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700">Department</h4>
        <p className="text-gray-900">{itemDetails?.department || 'N/A'}</p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700">Appointment Time</h4>
        <p className="text-gray-900">{new Date(itemDetails?.appointmentTime).toLocaleString() || 'N/A'}</p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700">Type</h4>
        <p className="text-gray-900">{itemDetails?.appointmentType || 'N/A'}</p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700">Status</h4>
        <p className="text-gray-900">{itemDetails?.status || 'N/A'}</p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700">Notes</h4>
        <p className="text-gray-900">{itemDetails?.notes || 'N/A'}</p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700">Organization</h4>
        <p className="text-gray-900">{itemDetails?.organization?.name || 'N/A'}</p>
      </div>
    </div>
  );

  return (
    <InfoComponent
      ref={ref}
      apiService={apiService}
      alertService={alertService}
      dataContext={appointmentContext}
      getItemName={getItemName}
      fetchItemDetails={fetchItemDetails}
      renderInfo={renderInfo}
    />
  );
});

// StaffInfoComponent
const StaffInfoComponent = forwardRef(({ apiService, alertService, staffContext }, ref) => {
  const getItemName = () => 'Staff';

 const fetchItemDetails = async (item, apiService) => {
  const query = `
    query GetStaffDetails($staffId: Int!) {
      staffMember(staffId: $staffId) {
        id
        name
        user {
          role
        }
        specialization
        department
        phone
        email
        hireDate
        salary
        workSchedule
        status
        organization {
          id
          name
        }
      }
    }
  `;
  const response = await apiService.graphqlRequest(query, { staffId: item.id });
  if (!response || !response.staffMember || response.errors) {
    throw new Error(response.errors?.[0]?.message || 'Failed to fetch staff details');
  }
  return {
    ...response.staffMember,
    role: response.staffMember.user.role, // Flatten role for convenience
  };
};

  const renderInfo = ({ selectedItem, itemDetails }) => (
    <div className="space-y-4">
      <div>
        <h4 className="text-sm font-medium text-gray-700">Name</h4>
        <p className="text-gray-900">{itemDetails?.name || 'N/A'}</p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700">Role</h4>
        <p className="text-gray-900">{itemDetails?.role || 'N/A'}</p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700">Specialization</h4>
        <p className="text-gray-900">{itemDetails?.specialization || 'N/A'}</p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700">Department</h4>
        <p className="text-gray-900">{itemDetails?.department || 'N/A'}</p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700">Phone</h4>
        <p className="text-gray-900">{itemDetails?.phone || 'N/A'}</p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700">Email</h4>
        <p className="text-gray-900">{itemDetails?.email || 'N/A'}</p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700">Hire Date</h4>
        <p className="text-gray-900">{itemDetails?.hireDate ? new Date(itemDetails.hireDate).toLocaleDateString() : 'N/A'}</p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700">Salary</h4>
        <p className="text-gray-900">{itemDetails?.salary ? `$${itemDetails.salary.toFixed(2)}` : 'N/A'}</p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700">Work Schedule</h4>
        <p className="text-gray-900">{itemDetails?.workSchedule || 'N/A'}</p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700">Status</h4>
        <p className="text-gray-900">{itemDetails?.status || 'N/A'}</p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700">Organization</h4>
        <p className="text-gray-900">{itemDetails?.organization?.name || 'N/A'}</p>
      </div>
    </div>
  );

  return (
    <InfoComponent
      ref={ref}
      apiService={apiService}
      alertService={alertService}
      dataContext={staffContext}
      getItemName={getItemName}
      fetchItemDetails={fetchItemDetails}
      renderInfo={renderInfo}
    />
  );
});

// InventoryInfoComponent
const InventoryInfoComponent = forwardRef(({ apiService, alertService, inventoryContext }, ref) => {
  const getItemName = () => 'Inventory Item';

  const fetchItemDetails = async (item, apiService) => {
    const query = `
      query GetInventoryItemDetails($itemId: Int!) {
        inventoryItem(itemId: $itemId) {
          id
          name
          itemType
          quantity
          unit
          expiryDate
          location
          supplier
          reorderThreshold
          costPrice
          sellingPrice
          status
          organization { id name }
        }
      }
    `;
    const response = await apiService.graphqlRequest(query, { itemId: item.id });
    if (!response || !response.inventoryItem || response.errors) {
      throw new Error(response.errors?.[0]?.message || 'Failed to fetch inventory item details');
    }
    if (response.inventoryItem.itemType === 'Medication') {
      const llmInfo = await apiService.getMedicationInfoLLM(response.inventoryItem.name);
      return { ...response.inventoryItem, llmInfo };
    }
    return response.inventoryItem;
  };

  const renderInfo = ({ selectedItem, itemDetails }) => (
    <div className="space-y-4">
      <div>
        <h4 className="text-sm font-medium text-gray-700">Item Name</h4>
        <p className="text-gray-900">{itemDetails?.name || 'N/A'}</p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700">Type</h4>
        <p className="text-gray-900">{itemDetails?.itemType || 'N/A'}</p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700">Quantity</h4>
        <p className="text-gray-900">{itemDetails?.quantity} {itemDetails?.unit || ''}</p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700">Expiry Date</h4>
        <p className="text-gray-900">{itemDetails?.expiryDate ? new Date(itemDetails.expiryDate).toLocaleDateString() : 'N/A'}</p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700">Location</h4>
        <p className="text-gray-900">{itemDetails?.location || 'N/A'}</p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700">Supplier</h4>
        <p className="text-gray-900">{itemDetails?.supplier || 'N/A'}</p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700">Reorder Threshold</h4>
        <p className="text-gray-900">{itemDetails?.reorderThreshold || 'N/A'}</p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700">Cost Price</h4>
        <p className="text-gray-900">{itemDetails?.costPrice ? `$${itemDetails.costPrice.toFixed(2)}` : 'N/A'}</p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700">Selling Price</h4>
        <p className="text-gray-900">{itemDetails?.sellingPrice ? `$${itemDetails.sellingPrice.toFixed(2)}` : 'N/A'}</p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700">Status</h4>
        <p className="text-gray-900">{itemDetails?.status || 'N/A'}</p>
      </div>
      <div>
        <h4 className="text-sm font-medium text-gray-700">Organization</h4>
        <p className="text-gray-900">{itemDetails?.organization?.name || 'N/A'}</p>
      </div>
      {itemDetails?.llmInfo && (
        <div>
          <h4 className="text-sm font-medium text-gray-700">Medication Information</h4>
          <p className="text-gray-900 whitespace-pre-wrap">{itemDetails.llmInfo}</p>
        </div>
      )}
    </div>
  );

  return (
    <InfoComponent
      ref={ref}
      apiService={apiService}
      alertService={alertService}
      dataContext={inventoryContext}
      getItemName={getItemName}
      fetchItemDetails={fetchItemDetails}
      renderInfo={renderInfo}
    />
  );
});

export { InfoComponent, PatientInfoComponent, AppointmentInfoComponent, StaffInfoComponent, InventoryInfoComponent };